import dayjs from "dayjs";
import { FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { FunctionCoach } from "../../../../../../../../api/services/program/models";
import {
  useDeleteProjectActivityTimeLineMutation,
  useGetParticipantActivitiesListByProjectId,
} from "../../../../../../../../api/services/project/projectApis";
import {
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiDateFieldFormController,
  MuiGrid,
  MuiSelect,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import PermissionWrapper from "../../../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  TimelineActivityDataItem,
  TimelineActivityType,
  ViewType,
} from "../../../../../../../../components/timelineActivity/TimelineActivity";
import { TimelineActivityTable } from "../../../../../../../../components/timelineActivityTable/TimelineActivityTable";
import {
  chipKey,
  DATE_FORMAT,
  gridSize,
  labelKey,
  MODULE_PERMISSIONS,
  viewMoreKey,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry, getDateFormatByCountryWithTime } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  convertToUserFormatWithLocalTime,
  getFullName,
  getValueOrBlank,
  getYesterdayDate,
  isDateInRange,
  isEmpty,
  isNotEmpty,
} from "../../../../../../../../helpers/utils";
import { useMediaQueries } from "../../../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { siteCoreContentLoadingSelector } from "../../../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ActivityTimelineFilterFormType,
  ProjectActivityResponse,
  ProjectActivityTimelineFilterFormKey,
  ShowFilterKeys,
} from "../../../../../../modelsAndSchemas/Models";
import { activityTimelineSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { AddActivity } from "../addActivity/AddActivity";
import { ActivityTimelineContainer } from "./ActivityTimeline.style";

const { AllKey, DurationKey, LastMonthKey, ThisMonthKey } = ShowFilterKeys;

interface ActivityTimelineFormProps {
  pageId: string;
  projectId: string;
  displayAddBtn?: boolean;
  projectCoachList?: FunctionCoach[];
  viewOnly?: boolean;
}

const ActivityTimeline: FC<ActivityTimelineFormProps> = ({
  pageId,
  projectId,
  displayAddBtn = true,
  projectCoachList,
  viewOnly = false,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const defaultValueDefault = {
    [ProjectActivityTimelineFilterFormKey.show]: AllKey,
  };
  const deleteIdRef = useRef<string>("");

  const { control, getValues, setValue, watch } =
    useForm<ActivityTimelineFilterFormType>({
      mode: "all",
      defaultValues: defaultValueDefault,
    });

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const [isOpenActivity, setIsOpenActivity] = useState(false);
  const tableRef = useRef<{ resetPagination: () => void }>(null);

  const [isEditActivity, setIsEditActivity] = useState(false);
  const [activityData, setActivityData] = useState<TimelineActivityType[]>([]);

  const { show: selectedFilter } = getValues();

  const { isMobile, isTablet } = useMediaQueries();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const yesterdayDate = getYesterdayDate();
  const [filteredActivityData, setFilteredActivityData] = useState<
    TimelineActivityType[]
  >([]);

  const [editActivityData, setEditActivityData] =
    useState<ProjectActivityResponse>();

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const getLabel = (key: string) => {
    return fetchLabel(activityTimelineSiteCoreKeys.activityTimeline, key);
  };

  const onCreateActivityClick = () => {
    setIsOpenActivity(true);
  };
  const dispatch = useDispatch();

  const { displayToast } = useToast();

  const {
    data: activityTimelineData,
    isFetching: isActivityTimelinePending,
    isError: isActivityTimelineError,
    isSuccess: isActivityTimelineSuccess,
    refetchWithParams: getActivityTimelineData,
  } = useGetParticipantActivitiesListByProjectId();

  const {
    mutateAsync: deleteActivity,
    isPending: isDeleteActivityPending,
    isSuccess: isDeleteActivitySuccess,
    isError: isDeleteActivityError,
  } = useDeleteProjectActivityTimeLineMutation();

  useEffect(() => {
    if (projectId) getActivityData();
  }, [projectId]);

  const getActivityData = () => {
    getActivityTimelineData(projectId);
  };

  const userCountryDateFormatTime = getDateFormatByCountryWithTime(
      getValueOrBlank(loggedInUser?.countryCode),
    );

  useEffect(() => {
    if (isActivityTimelinePending || isDeleteActivityPending) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isActivityTimelinePending, isDeleteActivityPending]);

  useEffect(() => {
    if (isActivityTimelineError || isDeleteActivityError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isActivityTimelineError, isDeleteActivityError]);

  useEffect(() => {
    if (
      isActivityTimelineSuccess &&
      isNotEmpty(activityTimelineData) &&
      !siteCoreLoading
    ) {
      const localData = activityTimelineData.map(
        (activity: ProjectActivityResponse) => ({
          timeStamp: convertToUserFormatWithLocalTime(
            activity.activityDate,
            userCountryDateFormatTime,
            loggedInUser?.timeZone,
          ),
          id: activity.id,
          title: activity?.activityName,
          creator: `${getLabel(activityTimelineSiteCoreKeys.activityTimelineBy)} ${getFullName(
            activity.createdBy.firstName,
            activity.createdBy.lastName,
            true,
          )}`,
          isCollapsible: true,
          expandableData: transformActivityData(activity),
          showMoreOptions: !viewOnly,
          isNotClosureType:
            activity.timelineType !== 4 &&
            activity.timelineType !== 5 &&
            activity.timelineType !== 3,
        }),
      );

      setActivityData(localData);
    } else {
      setActivityData([]);
    }
  }, [isActivityTimelineSuccess, activityTimelineData, siteCoreLoading]);

  useEffect(() => {
    if (isDeleteActivitySuccess) {
      getActivityData();
      displayToast(
        getLabel(activityTimelineSiteCoreKeys.activityTimelineDeletedMessage),
      );
    }
  }, [isDeleteActivitySuccess]);

  const getDurationValue = (duration?: string, listKey?: string) => {
    if (!duration && isEmpty(duration)) {
      return "";
    }
    if (listKey && isNotEmpty(listKey)) {
      const durationValue = fetchValueByKeyOfDropdown(
        activityTimelineSiteCoreKeys.activityTimeline,
        listKey,
        getValueOrBlank(duration),
      );
      return isNotEmpty(durationValue)
        ? `${durationValue} ${commonLabels.minutes}`
        : "";
    }
    return `${duration} ${commonLabels.minutes}`;
  };

  const transformActivityData = (
    activityData: ProjectActivityResponse,
  ): TimelineActivityDataItem[] => {
    const {
      activityName,
      modality,
      topics,
      notes,
      actualDurationInMinutes,
      defaultDurationInMinutes,
      onBehalfOfFacilitator,
      activityDate,
    } = activityData;

    const getDefaultLabels = () => [
      {
        key: activityTimelineSiteCoreKeys.activityTimelineActivityType,
        value: activityName,
      },
      {
        key: activityTimelineSiteCoreKeys.activityTimelineModality,
        value: getValueOrBlank(modality),
        listKey: activityTimelineSiteCoreKeys.activityTimelineModalityList,
      },
      {
        key: activityTimelineSiteCoreKeys.activityTimelineTopic,
        value:
          topics?.map((topic: { name: string; id: string }) => ({
            label: topic.name,
            value: topic.id,
          })) ?? [],
      },
      {
        key: activityTimelineSiteCoreKeys.activityTimelineDefaultDuration,
        value: getDurationValue(defaultDurationInMinutes?.toString()),
      },
      {
        key: activityTimelineSiteCoreKeys.activityTimelineActualDuration,
        value: getDurationValue(
          actualDurationInMinutes,
          activityTimelineSiteCoreKeys.activityTimelineActualDurationMinutesList,
        ),
      },
      {
        key: activityTimelineSiteCoreKeys.activityTimelineActivityDate,
        value: convertToUserFormat(activityDate, userCountryDateFormat),
      },
      {
        key: activityTimelineSiteCoreKeys.activityTimelineOnBehalfOf,
        value: getValueOrBlank(
          getFullName(
            onBehalfOfFacilitator?.firstName,
            onBehalfOfFacilitator?.lastName,
          ),
        ),
      },
      {
        key: commonLabels.notes,
        value: getValueOrBlank(notes),
        type: viewMoreKey as ViewType,
      },
    ];

    let labels: {
      key: string;
      value: string | Array<{ label: string; value: string }>;
      listKey?: string;
      type?: ViewType;
    }[];
    labels = getDefaultLabels();
    return labels.filter(Boolean).map(({ key, value, listKey, type }) => {
      if (Array.isArray(value)) {
        return {
          type: chipKey,
          label: getLabel(key),
          value,
        };
      }

      return {
        type: type ?? labelKey,
        label: key === commonLabels.notes ? key : getLabel(key),
        value: listKey
          ? fetchValueByKeyOfDropdown(
              activityTimelineSiteCoreKeys.activityTimeline,
              listKey,
              value ?? "",
            )
          : value,
      };
    });
  };

  useEffect(() => {
    applyFilters();
  }, [watch(ProjectActivityTimelineFilterFormKey.show), activityData]);

  const onGoBtnClick = () => {
    applyFilters();
  };

  const applyFilters = () => {
    const currentDate = dayjs();
    const startOfCurrentMonth = currentDate.startOf("month");
    const endOfCurrentMonth = currentDate.endOf("month");
    const startOfLastMonth = currentDate.subtract(1, "month").startOf("month");
    const endOfLastMonth = currentDate.subtract(1, "month").endOf("month");

    resetPagination();

    if (selectedFilter !== DurationKey) {
      setValue(ProjectActivityTimelineFilterFormKey.fromDate, "");
      setValue(ProjectActivityTimelineFilterFormKey.toDate, "");
    }
    const fromDate = getValues(ProjectActivityTimelineFilterFormKey.fromDate);
    const toDate = getValues(ProjectActivityTimelineFilterFormKey.toDate);

    const filteredData = activityData.filter((projectActivity) => {
      const activityDate = dayjs(
        projectActivity.timeStamp,
        userCountryDateFormat,
      );

      if (selectedFilter === AllKey) {
        return true; // Show all activities
      } else if (selectedFilter === ThisMonthKey) {
        // Show activities from this month
        return isDateInRange(
          activityDate,
          startOfCurrentMonth,
          endOfCurrentMonth,
        );
      } else if (selectedFilter === LastMonthKey) {
        // Show activities from last month
        return isDateInRange(activityDate, startOfLastMonth, endOfLastMonth);
      } else if (selectedFilter === DurationKey && fromDate && toDate) {
        // Show activities between selected 'fromDate' and 'toDate'
        const dayjsFromDate = dayjs(fromDate, userCountryDateFormat);
        const dayjsToDate = dayjs(toDate, userCountryDateFormat);
        return isDateInRange(activityDate, dayjsFromDate, dayjsToDate);
      } else {
        return true;
      }
    });

    setFilteredActivityData(filteredData);
  };

  const handleEditActivity = (id: string) => {
    const activity = activityTimelineData.find(
      (activity: ProjectActivityResponse) => activity.id === id,
    );
    if (activity) {
      setIsEditActivity(true);
      setEditActivityData(activity);
      setIsOpenActivity(true);
    }
  };

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const handleDeleteActivity = (id: string) => {
    deleteIdRef.current = id;
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteClick = () => {
    if (isNotEmpty(deleteIdRef.current)) {
      let idToDelete = deleteIdRef.current;
      deleteActivity(idToDelete);
      handleModalClose();
    }
  };

  const handleModalClose = () => {
    if (showDeleteConfirmModal) {
      setShowDeleteConfirmModal(false);
    }
  };

  const handleActivityModalClose = () => {
    setIsOpenActivity(false);
    setIsEditActivity(false);
    setEditActivityData(undefined);
  };

  return (
    <ActivityTimelineContainer>
      <MuiBox className="activity-timeline" data-testid="activity-timeline">
        <MuiBox className="filter-wrapper">
          <MuiBox className="filterBy">
            <FormController
              control={control}
              name={ProjectActivityTimelineFilterFormKey.filterBy}
            >
              <MuiSelect
                label={getLabel(
                  activityTimelineSiteCoreKeys.activityTimelineFilterByCategoryDropDown,
                )}
                options={fetchDropdownValues(
                  activityTimelineSiteCoreKeys.activityTimeline,
                  activityTimelineSiteCoreKeys.activityTimelineFilterByCategoryList,
                )}
                isHorizontalLabel={!isMobile && !isTablet}
                value=""
                id={`${pageId}-${ProjectActivityTimelineFilterFormKey.filterBy}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiBox>
          <MuiBox className="modality">
            <FormController
              control={control}
              name={ProjectActivityTimelineFilterFormKey.show}
            >
              <MuiSelect
                label={commonLabels.show}
                options={fetchDropdownValues(
                  activityTimelineSiteCoreKeys.activityTimeline,
                  activityTimelineSiteCoreKeys.activityTimelineShowFilterList,
                )}
                isHorizontalLabel={!isMobile && !isTablet}
                value=""
                id={`${pageId}-${ProjectActivityTimelineFilterFormKey.show}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiBox>

          {displayAddBtn && (
            <PermissionWrapper
              permission={MODULE_PERMISSIONS.PROJECT_ADD_ACTIVITY_TIMELINE}
            >
              <MuiBox className="add-activity-wrapper">
                <MuiButtonSecondary
                  data-testid={`${pageId}-create-activity`}
                  onClick={onCreateActivityClick}
                >
                  {getLabel(
                    activityTimelineSiteCoreKeys.activityTimelineAddActivityButton,
                  )}
                </MuiButtonSecondary>
              </MuiBox>
            </PermissionWrapper>
          )}
        </MuiBox>
        {selectedFilter === DurationKey && (
          <MuiBox className="duration-picker-wrapper">
            <MuiGrid container>
              <MuiGrid size={gridSize}>
                <MuiDateFieldFormController
                  control={control}
                  name={ProjectActivityTimelineFilterFormKey.fromDate}
                  label={getLabel(
                    activityTimelineSiteCoreKeys.activityTimelineFromDate,
                  )}
                  valueFormat={userCountryDateFormat}
                  format={userCountryDateFormat}
                  className="date-picker"
                  maxDate={yesterdayDate}
                  id={`${pageId}-${ProjectActivityTimelineFilterFormKey.fromDate}`}
                  disableManualInput
                  value={
                    getValues(ProjectActivityTimelineFilterFormKey.fromDate) !==
                    ""
                      ? dayjs(
                          getValues(
                            ProjectActivityTimelineFilterFormKey.fromDate,
                          ),
                          userCountryDateFormat,
                        )
                      : null
                  }
                  requiredARIALabel={commonARIALabel.required}
                  selectDateARIALabel={commonARIALabel.selectDate}
                />
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <MuiDateFieldFormController
                  control={control}
                  name={ProjectActivityTimelineFilterFormKey.toDate}
                  label={getLabel(
                    activityTimelineSiteCoreKeys.activityTimelineToDate,
                  )}
                  valueFormat={userCountryDateFormat}
                  format={userCountryDateFormat}
                  className="date-picker"
                  disableFuture
                  id={`${pageId}-${ProjectActivityTimelineFilterFormKey.toDate}`}
                  disableManualInput
                  value={
                    getValues(ProjectActivityTimelineFilterFormKey.toDate) !==
                    ""
                      ? dayjs(
                          getValues(
                            ProjectActivityTimelineFilterFormKey.toDate,
                          ),
                          userCountryDateFormat,
                        )
                      : null
                  }
                  requiredARIALabel={commonARIALabel.required}
                  selectDateARIALabel={commonARIALabel.selectDate}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className="go-btn-grid">
                <MuiButtonPrimary
                  onClick={onGoBtnClick}
                  id={`${pageId}-duration-go-btn`}
                >
                  {commonLabels.go}
                </MuiButtonPrimary>
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
        )}

        <TimelineActivityTable
          ref={tableRef}
          activityData={filteredActivityData}
          handleEdit={handleEditActivity}
          handleDelete={handleDeleteActivity}
          pageId={pageId}
          noDataInApi={isActivityTimelineSuccess && isEmpty(activityData)}
          emptyMessage={getLabel(
            activityTimelineSiteCoreKeys.activityTimelineNoActivitiesAvailable,
          )}
          hasDeletePermission={hasModulePermission(
            MODULE_PERMISSIONS.PROJECT_DELETE_ACTIVITY_TIMELINE,
          )}
        />

        {isOpenActivity && (
          <AddActivity
            show={isOpenActivity}
            handleCloseModal={handleActivityModalClose}
            pageId={pageId}
            projectId={projectId}
            getListOfActivitiesApi={() => getActivityData()}
            isEditActivity={isEditActivity}
            editActivityData={editActivityData}
            projectCoachList={projectCoachList}
          />
        )}

        {showDeleteConfirmModal && (
          <WarningConfirmationModal
            show={showDeleteConfirmModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleDeleteClick}
            labelContent={getLabel(
              activityTimelineSiteCoreKeys.deleteActivityConfirmationText,
            )}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </MuiBox>
    </ActivityTimelineContainer>
  );
};

export default ActivityTimeline;
